import { Injectable } from '@angular/core';
import { ApiService } from 'src/app/services/api.service';
import { ToastrService } from 'ngx-toastr';
import { DialogService } from 'src/app/core/services/dialog.service';
import { PopUpDialogService } from 'src/app/services/popup-dialog.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ITEM_TYPE_OPTIONS } from 'src/app/lib/constants/dropdown-options.constants';
import { RequestsService } from 'src/app/core/services/http/requests.service';
import { Urls } from 'src/app/lib/urls';
import { firstValueFrom } from 'rxjs';
import { UpdateBuyLoadItemRequest } from 'src/app/core/model/server/vo/UpdateBuyLoadItemRequest.interface';

@Injectable({
  providedIn: 'root'
})
export class SkuService {

  GamingPins = ITEM_TYPE_OPTIONS.find(item => item.label === 'Pins');

  constructor(
    private apiService: ApiService,
    private toastr: ToastrService,
    private dialogService: DialogService,
    private spinner: NgxSpinnerService,
    private PopUpDialog: PopUpDialogService,
    private request: RequestsService
  ) { }


  setValue(text: string, value: any): { text: string, value: any } {
    return { text, value };
  }

  // =============================================
  // PROVIDER VALIDATION LOGIC
  // =============================================

  checkGamingPinsLogic(
    text: string,
    value: any,
    currentIsGamingPins: boolean
  ): {
    isGamingPins: boolean,
    shouldResetProviderType: boolean,
    autoSetProviderType?: { text: string, value: any }
  } {
    const newIsGamingPins = (value === this.GamingPins.value && text === this.GamingPins.label);
    const shouldResetProviderType = (!newIsGamingPins && currentIsGamingPins);

    const result: any = {
      isGamingPins: newIsGamingPins,
      shouldResetProviderType
    };

    // Auto-set provider type to "All" for gaming pins
    if (newIsGamingPins) {
      result.autoSetProviderType = { text: 'All', value: '' };
    }

    return result;
  }
  
  async checkItemProviderExistence(itemId: string, provider: string | number): Promise<boolean> {
    try {
      const params = { search: itemId, sortBy: {} };
      console.log('=== Checking Provider Existence ===');

      const response = await this.request.post(Urls.buyload + '?page=1&size=100', params);

      if (response.status === 200 && response.result.data) {
        const existingItems = response.result.data.filter(item => item.itemId === itemId);
        
        // Ensure strictly comparable numbers
        const providerToCheck = Number(provider);
        const providerExists = existingItems.some(item => Number(item.provider) === providerToCheck);

        return providerExists;
      }
      return false;
    } catch (error) {
      return false;
    }
  }

  // =============================================
  // CATEGORY LOGIC
  // =============================================

  async getEnrichedCategoryOptions(currentCategory: string): Promise<any[]> {
    let categoryOptions = [{ label: '== Select an option ==', value: null }];

    try {
      const response = await firstValueFrom(
        this.apiService.getActiveCategories(1, 100000, { isEnabled: true })
      );

      if (response.status === 200 && response.result.data) {
        const activeCategories = response.result.data.map(category => ({
          label: category.categoryDescription,
          value: category.itemCategory
        }));
        categoryOptions = [...categoryOptions, ...activeCategories];
      }

      const categoryExists = categoryOptions.some(option => option.value === currentCategory);
      if (!categoryExists && currentCategory) {
        const allCatsResponse = await firstValueFrom(
          this.apiService.getActiveCategories(1, 100000, null)
        );

        if (allCatsResponse.status === 200 && allCatsResponse.result.data) {
          const missingCat = allCatsResponse.result.data.find(c => c.itemCategory === currentCategory);
          if (missingCat) {
            categoryOptions.splice(1, 0, {
              label: `${missingCat.categoryDescription}`,
              value: missingCat.itemCategory
            });
          }
        }
      }

      return categoryOptions;

    } catch (error) {
      this.toastr.error(
        `Please refresh or contact the developer if the error still occurs`,
        'Something went wrong'
      );
      return categoryOptions;
    }
  }

  // =============================================
  // UPDATE LOGIC
  // =============================================

  async updateSkuItem(
    uid: number, 
    formValues: any,
    isGamingPins: boolean,
    provider: any,
    providerType: any,
    itemCategory: any,
    itemType: any,
    redeemable: boolean 
  ): Promise<boolean> {
    
    this.spinner.show().catch(err => console.error(err));

    try {
      let response;

      if (isGamingPins || itemType === 8) {
        const gamingPinsRequestBody = {
          provider,
          providerTypes: Array.isArray(providerType) ? providerType : [],
          itemId: formValues.itemId,
          itemName: formValues.itemName,
          itemCategory,
          itemDesc: formValues.itemDesc,
          itemPrice: formValues.itemPrice,
          sort: null,
          coinsType: null,
          redeemable
        };

        response = await firstValueFrom(
          this.apiService.updateBuyloadItemPins(gamingPinsRequestBody)
        );

      } else {
        const requestBody: UpdateBuyLoadItemRequest = {
          itemName: formValues.itemName,
          itemId: formValues.itemId,
          itemDesc: formValues.itemDesc,
          itemPrice: formValues.itemPrice,
          provider,
          providerType,
          itemType,
          redeemable 
        };

        // FIX: Explicitly convert 'uid' to Number() here to satisfy TypeScript
        response = await firstValueFrom(
          this.apiService.updateBuyLoadItem(Number(uid), requestBody)
        );
      }

      if (response.status === 200) {
        this.toastr.success(response.message + '!', 'Success!');
        this.spinner.hide();
        return true;
      } else {
        this.toastr.error(response.message + '!', 'Something went wrong!');
        this.spinner.hide();
        return false;
      }

    } catch (error) {
      console.error(error);
      this.toastr.error(error.error?.message || 'Something went wrong!', 'Error!');
      this.spinner.hide();
      return false;
    }
  }

  // =============================================
  // TOGGLE STATUS LOGIC
  // =============================================

  toggleStatus(
    id: number,
    value: number,
    action: 'enable' | 'disable',
    refreshFn: () => void
  ): void {
    const messages = {
      enable: 'Please be advised that the SKU will become active if enabled',
      disable: 'Please be advised that the SKU will become inactive if disabled',
    };

    this.PopUpDialog.openStatusActionDialog(
      messages[action],
      action.toUpperCase(),
      'CANCEL',
      async () => {
        await this.performToggleStatus(id, value, action, refreshFn);
      },
      () => console.log(`${action} cancelled`)
    );
  }

  private async performToggleStatus(
    id: number,
    value: number,
    action: 'enable' | 'disable',
    refreshFn: () => void,
    itemName?: string
  ): Promise<void> {
    try {
      this.spinner.show();
      const request = { status: value };
      const response = await this.apiService.updateBuyLoadItem(id, request).toPromise();

      if (response.status === 200) {
        const successMessage = itemName
          ? `SKU "${itemName}" has been ${action}d successfully!`
          : `SKU has been ${action}d successfully!`;
        this.PopUpDialog.openSuccessDialog(successMessage);
        refreshFn();
      } else {
        this.PopUpDialog.openErrorDialog(response.message || 'Something went wrong!');
      }
    } catch (error) {
      const errorMessage = error?.error?.message || error?.message || `Failed to ${action} SKU`;
      this.PopUpDialog.openErrorDialog(errorMessage);
    } finally {
      this.spinner.hide();
    }
  }
}
